import React from 'react';
import { Modal, Button, Typography, Space, Tag, Table, List, Empty, Badge } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { SourceProcess, ExtractedCharacter } from '../../../types';

const { Text } = Typography;

interface ExtractCharactersModalProps {
  visible: boolean;
  onCancel: () => void;
  process: SourceProcess | null;
  extractedCharacters: ExtractedCharacter[];
  onSave: () => void;
  loading: boolean;
}

const ExtractCharactersModal: React.FC<ExtractCharactersModalProps> = ({
  visible,
  onCancel,
  process,
  extractedCharacters,
  onSave,
  loading,
}) => {
  return (
    <Modal
      title={`AI Extracted Characters - ${process?.name}`}
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={onSave}
          loading={loading}
        >
          Save Characters and Update Process
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          AI has detected the following characters in this process.
          New characters will be created, and all listed characters will be associated with this process.
        </Text>
      </div>
      <Table
        dataSource={extractedCharacters}
        rowKey={(_, index) => index!.toString()}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: '8px 16px', background: '#f9f9f9', borderRadius: 4 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Detected Checkpoints (Appearance Variants):</Text>
              {record.checkpoints && record.checkpoints.length > 0 ? (
                <List
                  size="small"
                  dataSource={record.checkpoints}
                  renderItem={(cp) => (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Space>
                          <Tag color={cp.isNew ? 'green' : 'blue'}>
                            {cp.isNew ? 'NEW CHECKPOINT' : 'EXISTING'}
                          </Tag>
                          <Text strong>{cp.name}</Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{cp.description}</Text>
                        {cp.reason && <Text italic style={{ fontSize: '11px', color: '#8c8c8c' }}>Reason: {cp.reason}</Text>}
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No specific checkpoints detected" />
              )}
            </div>
          ),
          rowExpandable: (record) => !!record.checkpoints && record.checkpoints.length > 0,
          defaultExpandAllRows: true,
        }}
        columns={[
          {
            title: 'Character',
            key: 'name',
            render: (_, record) => (
              <Space>
                <Tag color={record.isNew ? 'green' : 'blue'}>
                  {record.isNew ? 'NEW CHARACTER' : 'EXISTING'}
                </Tag>
                <Text strong>{record.name}</Text>
              </Space>
            ),
          },
          {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (role) => <Tag>{role}</Tag>,
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
          },
          {
            title: 'Variants',
            key: 'checkpointsCount',
            render: (_, record) => (
              <Badge
                count={record.checkpoints?.length || 0}
                showZero
                color={record.checkpoints?.some(cp => cp.isNew) ? '#52c41a' : '#1890ff'}
              />
            ),
          }
        ]}
      />
    </Modal>
  );
};

export default ExtractCharactersModal;
